import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiRequest, queryClient } from "@/lib/queryClient";

interface ConfirmResponse {
  ok: boolean;
  applied?: boolean;
  alreadyApplied?: boolean;
  pending?: boolean;
  tier?: string;
  billingPeriod?: string;
  message?: string;
}

export default function UpgradeSuccessPage() {
  const [, setLocation] = useLocation();
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");
  const updatedExisting = params.get("updated") === "1";
  const presetTier = params.get("tier");

  const [status, setStatus] = useState<"loading" | "success" | "pending" | "error">(
    updatedExisting ? "success" : sessionId ? "loading" : "error",
  );
  const [data, setData] = useState<ConfirmResponse | null>(
    updatedExisting ? { ok: true, applied: true, tier: presetTier || undefined } : null,
  );
  const [errorMsg, setErrorMsg] = useState<string>("");

  useEffect(() => {
    if (updatedExisting) {
      // In-place subscription update — no Stripe session to confirm.
      queryClient.invalidateQueries();
      return;
    }
    if (!sessionId) {
      setErrorMsg("No session ID provided.");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiRequest("GET", `/api/upgrade/confirm?session_id=${encodeURIComponent(sessionId)}`);
        const json = (await res.json()) as ConfirmResponse;
        if (cancelled) return;
        if (json.pending) {
          setStatus("pending");
          setData(json);
        } else if (json.ok) {
          setStatus("success");
          setData(json);
          queryClient.invalidateQueries();
        } else {
          setStatus("error");
          setErrorMsg(json.message || "Couldn't verify your upgrade.");
        }
      } catch (err: any) {
        if (cancelled) return;
        setStatus("error");
        setErrorMsg(err?.message || "Couldn't verify your upgrade.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, updatedExisting]);

  return (
    <div className="container max-w-xl mx-auto p-4 md:p-8 min-h-screen flex items-center" data-testid="page-upgrade-success">
      <Card className="w-full">
        <CardHeader className="text-center pb-4">
          {status === "loading" && (
            <>
              <Loader2 className="h-12 w-12 mx-auto mb-3 animate-spin text-muted-foreground" />
              <CardTitle>Confirming your upgrade…</CardTitle>
              <CardDescription>This usually takes just a few seconds.</CardDescription>
            </>
          )}
          {status === "success" && (
            <>
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-600 dark:text-green-400" />
              <CardTitle data-testid="text-success-title">You're upgraded!</CardTitle>
              <CardDescription>
                Welcome to{" "}
                <Badge variant="default" className="capitalize ml-1" data-testid="badge-new-tier">
                  {data?.tier || "your new plan"}
                </Badge>
                {data?.billingPeriod ? <> — billed {data.billingPeriod}.</> : "."}
              </CardDescription>
            </>
          )}
          {status === "pending" && (
            <>
              <Loader2 className="h-12 w-12 mx-auto mb-3 animate-spin text-amber-500" />
              <CardTitle>Payment processing</CardTitle>
              <CardDescription>
                Your payment is going through. We'll activate your new plan as soon as it clears — usually within a minute.
              </CardDescription>
            </>
          )}
          {status === "error" && (
            <>
              <AlertCircle className="h-12 w-12 mx-auto mb-3 text-destructive" />
              <CardTitle>Couldn't verify your upgrade</CardTitle>
              <CardDescription className="text-destructive" data-testid="text-error-message">
                {errorMsg || "Something went wrong."}
              </CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {status === "success" && (
            <>
              <p className="text-sm text-muted-foreground text-center">
                Your new limits are active immediately. A receipt has been emailed to you.
              </p>
              <Button
                className="w-full"
                onClick={() => setLocation("/")}
                data-testid="button-go-to-dashboard"
              >
                Go to dashboard
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </>
          )}
          {status === "pending" && (
            <>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => window.location.reload()}
                data-testid="button-recheck"
              >
                Check again
              </Button>
              <Button
                className="w-full"
                variant="ghost"
                onClick={() => setLocation("/")}
                data-testid="button-go-home-pending"
              >
                Go to dashboard
              </Button>
            </>
          )}
          {status === "error" && (
            <>
              <p className="text-sm text-muted-foreground text-center">
                If you completed payment, your upgrade may still be processing. You can also email{" "}
                <a href="mailto:admin@clarik.io" className="underline">
                  admin@clarik.io
                </a>{" "}
                with this session ID:
              </p>
              {sessionId && (
                <p className="text-xs font-mono bg-muted p-2 rounded text-center" data-testid="text-session-id">
                  {sessionId}
                </p>
              )}
              <Button
                className="w-full"
                variant="outline"
                onClick={() => setLocation("/upgrade")}
                data-testid="button-back-to-plans"
              >
                Back to plans
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
